import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Field } from '../../../auth/field';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchLanguages } from '@eliadmin/core/src/api/general/general-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { RiDeleteBinLine } from 'react-icons/ri';
import { FiPlus } from 'react-icons/fi';
import { useEffect } from 'react';

export enum LanguageLevels {
  A1 = 'A1',
  A2 = 'A2',
  B1 = 'B1',
  B2 = 'B2',
  C1 = 'C1',
  C2 = 'C2',
}

const languageLevelsValues = [
  { label: 'text982', value: LanguageLevels.A1 },
  { label: 'text983', value: LanguageLevels.A2 },
  { label: 'text984', value: LanguageLevels.B1 },
  { label: 'text985', value: LanguageLevels.B2 },
  { label: 'text986', value: LanguageLevels.C1 },
  { label: 'text987', value: LanguageLevels.C2 },
];

export const AddOfferStep3 = () => {
  const { t } = useTranslations();
  const locale = useLocaleStore(state => state.currentLocale);

  const { data: languages } = useQuery({
    queryKey: [CacheKey.LANGUAGES],
    queryFn: () => fetchLanguages().then(r => r.data.map(l => ({ label: l.name, value: l.id }))),
  });

  const form = useFormContext();
  const {
    fields: languageFields,
    append: appendLanguage,
    remove: removeLanguage,
  } = useFieldArray({
    control: form.control,
    name: 'languages',
  });

  useEffect(() => {
    if (!form.getValues('languages')?.length) appendLanguage({ language: null, level: null });
  }, [languageFields.length, appendLanguage]);

  return (
    <div className="flex flex-col gap-6 bg-white p-6 md:gap-8 md:rounded-lg md:p-8">
      <h1 className="text-2xl font-bold md:text-4xl">{t('text1730')}</h1>
      <p className="text-[#63626F]">{t('text1772')}</p>
      {languageFields.map((field, index) => (
        <div className="grid grid-cols-12 items-center gap-4" key={field.id}>
          {/* Language Select */}
          <div className="col-span-12 md:col-span-6">
            <Field
              label={t('text989')}
              control={form.control}
              name={`languages[${index}].language`}
              type="select"
              options={languages || []}
              placeholder={t('text901')}
            />
          </div>

          {/* Level Select */}
          <div className="col-span-11 md:col-span-4">
            <Field
              label={t('text1781')}
              control={form.control}
              name={`languages[${index}].level`}
              type="select"
              options={
                languageLevelsValues.map(language => {
                  return {
                    ...language,
                    label: t(language.label),
                  };
                }) || []
              }
            />
          </div>
          <div className="col-span-1 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => removeLanguage(index)}
              disabled={languageFields.length === 1}
              className="flex h-12 w-12 items-center justify-center rounded-full p-0"
            >
              <RiDeleteBinLine className="h-5 w-5" />
            </Button>
          </div>
        </div>
      ))}
      <Button
        type="button"
        onClick={
          () => appendLanguage({ language: null, level: null }) // Initial empty values for a new experience
        }
        className="mt-4 flex max-w-min gap-2 whitespace-nowrap"
      >
        <FiPlus className="h-5 w-5" /> {t('text1776')}
      </Button>
    </div>
  );
};
